



































import { Component, Mixins } from 'vue-property-decorator';
import { Severity } from '@sentry/types';
import MixinPage from '@/plugins/podolib/services/helper/mixins/page';
import MixinAuth from '@/plugins/podolib/services/helper/mixins/auth';

import { TForm } from '@/plugins/podolib/lib/types';
import { ValidationProvider } from 'vee-validate/dist/types/components/Provider';

import AuthCard from '@/components/auth/AuthCard.vue';
import AuthFieldPassword from '@/components/auth/AuthFieldPassword.vue';
import AuthFieldAcceptDpa from '@/components/auth/AuthFieldAcceptDpa.vue';
import AuthFieldAcceptTou from '@/components/auth/AuthFieldAcceptTou.vue';

type TFields = {
  email: {
    value: string;
  };
  password: {
    value: string;
    hide: boolean;
  };
  accept_dpa: {
    value: boolean;
  };
  accept_tou: {
    value: boolean;
  };
};

@Component({
  components: {
    AuthFieldPassword,
    AuthFieldAcceptDpa,
    AuthFieldAcceptTou,
    AuthCard,
  },
})
export default class PageAuthSignUp extends Mixins(MixinPage, MixinAuth) {
  $refs!: {
    card: InstanceType<typeof AuthCard> & {
      $refs: {
        observer: InstanceType<typeof ValidationProvider>;
        form: HTMLFormElement;
      };
    };
  };

  // Form payload
  form: TForm<TFields> = {
    fields: {
      email: {
        value: null!,
      },
      password: {
        value: null!,
        hide: true,
      },
      accept_dpa: {
        value: false,
      },
      accept_tou: {
        value: false,
      },
    },
    pending: false,
    error: null,
    success: false,
  };

  // Used with 'UsernameExistsException' error
  emailCache: string = null!;

  get passwordRequirements() {
    const items = {
      length: {
        label: String(this.$t('commons.sentences.at-least-characters--s', { value: 8 })),
        valide: false,
      },
      number: {
        label: String(this.$t('commons.sentences.must-contain-number--s')),
        valide: false,
      },
      uppercase: {
        label: String(this.$t('commons.sentences.must-contain-uppercase--s')),
        valide: false,
      },
      special: {
        label: String(this.$t('commons.sentences.must-contain-special--s')),
        valide: false,
      },
      lowercase: {
        label: String(this.$t('commons.sentences.must-contain-lowercase--s')),
        valide: false,
      },
    };

    const p = this.form.fields.password.value || '';
    let value = 0;
    let color;

    // Lowercase letter
    if (/[a-z]/gm.test(p)) {
      value += 10;
      items.lowercase.valide = true;
    }
    // Uppercase letter
    if (/[A-Z]/gm.test(p)) {
      value += 10;
      items.uppercase.valide = true;
    }
    // Number
    if (/[0-9]/gm.test(p)) {
      value += 10;
      items.number.valide = true;
    }
    // Special character
    if (/[^A-Za-z0-9\s]/gm.test(p)) {
      value += 10;
      items.special.valide = true;
    }

    value += p.length < 8 ? p.length * 7.5 : 60;
    if (p.length >= 8) {
      items.length.valide = true;
    }

    switch (true) {
      case value >= 100:
        color = 'success';
        break;
      case value >= 60:
        color = 'warning';
        break;
      default:
        color = 'error';
        break;
    }

    return {
      items,
      value,
      color,
    };
  }

  async signUp() {
    // Avoid duplication of the process
    if (this.form.pending) return;
    try {
      // Validate form...
      const isValid = await this.$refs.card.$refs.observer.validate();
      // If form is invalid, stop
      if (!isValid) return;

      // Reset flags
      this.form.success = false;
      this.form.error = null;
      // Set 'pending' flag
      this.form.pending = true;

      // Try to sign up ...
      await this.$pdc.cognito!.signUp({
        username: this.form.fields.email.value,
        password: this.form.fields.password.value,
      });
      // Analytics
      this.$pdc.analytics?.actions.trackSuccess('User SignedUp', {
        email: this.form.fields.email.value,
        ...(this as any).campaignQueryParamsToEvent,
      });
      // Set 'success' flag
      this.form.success = true;
      // Redirect to confirmation...
      await this.$router.push({ name: 'auth-confirm-sign-up', params: { email: this.form.fields.email.value } });
    } catch (e) {
      // Analytics
      this.$pdc.analytics?.actions.trackFailure('User SignedUp', e, {
        email: this.form.fields.email.value,
        ...(this as any).campaignQueryParamsToEvent,
      });
      if (e.code === 'UsernameExistsException') {
        // Set email cache
        this.emailCache = this.form.fields.email.value;
        // Set error
        this.$refs.card.$refs.observer.setErrors({
          // @ts-ignore Bad typing from vee-validate
          email: [e.message],
        });
        // Sentry
        this.$sentry.captureMessage(e.message, Severity.Log);
      } else {
        this.form.error = e;
        // Sentry
        this.$sentry.captureMessage(e.message, Severity.Critical);
      }
    } finally {
      // Reset 'pending' flag
      this.form.pending = false;
    }
  }
}
